import type { MachineDef, MachineId, Rarity } from '../core/types';

/**
 * Machines Gachapon — spec §8.3 (mise à jour GD) / MVP_DESIGN §3.4.
 * v4 : 60 / 30 / 10 — v6 : 55 / 25 / 15 / 5 — v12 : 55 / 25 / 15 / 4 / 1
 *
 * v0.8.0 (COMMENTS v0.7.0 §2.2) : les prix baissent à **15 / 45 / 135 or** —
 * « les dés coûtent un peu trop chers maintenant que les gains d'or sont
 * diminués » (`goldLevelGrowth` 4,4 → 1,3, §2.1). Le « 1180 » de la demande est
 * lu comme une coquille pour 180, d'où 135 (§F).
 *
 * v0.26.0 (COMMENTS v0.25.0 §1.2) : la **GachaDice v20** — d20 à **540 or**,
 * 55 / 25 / 15 / 4 / 0,9 / 0,1, débloquée par `shop_v20`. C'était alors la seule
 * machine à donner des **Prismatiques** : un Prismatique tiré est toujours
 * l'un des trois dés nommés (A72-14, `core/dice.ts` `createNamedPrismatic`).
 *
 * v0.43.0 (A89-1) : la **GachaDice v100** (d100 à 1 620 or, nœud
 * *Centésimal*) ; **retirée** en v0.45 (A93) avec son nœud — le d100 reste un
 * dé valide du moteur, plus aucune machine ne le donne. La v20 est de nouveau
 * la seule machine à donner des Prismatiques.
 *
 * v0.46.0 (A94-13) : les quatre **Corrupted GachaDice** (`c4` … `c20`),
 * placées **après** les machines de base (`machineForSides` rend la première
 * machine d'un nombre de faces : le recyclage garde le prix de base). Mêmes
 * faces et mêmes poids que la machine de base, ouvertes par *Artisan des dés*,
 * prix calculé (`costPerDie: 0`, `core/economy.ts : corruptedUnitCost`) ; leurs
 * dés portent un niveau, un Matériau, et peuvent être Enrichis (`core/gacha.ts`).
 */
const BASE_MACHINES: MachineDef[] = [
  {
    id: 'v4',
    name: 'GachaDice v4',
    sides: 4,
    costPerDie: 15,
    weights: { common: 60, uncommon: 30, rare: 10, epic: 0, legendary: 0, prismatic: 0 },
    requiresNode: null,
  },
  {
    id: 'v6',
    name: 'GachaDice v6',
    sides: 6,
    costPerDie: 45,
    weights: { common: 55, uncommon: 25, rare: 15, epic: 5, legendary: 0, prismatic: 0 },
    requiresNode: 'shop_v6',
  },
  {
    id: 'v12',
    name: 'GachaDice v12',
    sides: 12,
    costPerDie: 135,
    weights: { common: 55, uncommon: 25, rare: 15, epic: 4, legendary: 1, prismatic: 0 },
    requiresNode: 'shop_v12',
  },
  {
    id: 'v20',
    name: 'GachaDice v20',
    sides: 20,
    costPerDie: 540,
    weights: { common: 55, uncommon: 25, rare: 15, epic: 4, legendary: 0.9, prismatic: 0.1 },
    requiresNode: 'shop_v20',
  },
];

/** *Corrupted GachaDice* d'une machine de base (A94-13). */
function corruptedOf(base: MachineDef, id: MachineId): MachineDef {
  return {
    id,
    name: `Corrupted ${base.name}`,
    sides: base.sides,
    costPerDie: 0,
    weights: { ...base.weights },
    requiresNode: 'aby_artisan',
    corrupted: true,
  };
}

export const MACHINES: MachineDef[] = [
  ...BASE_MACHINES,
  corruptedOf(BASE_MACHINES[0], 'c4'),
  corruptedOf(BASE_MACHINES[1], 'c6'),
  corruptedOf(BASE_MACHINES[2], 'c12'),
  corruptedOf(BASE_MACHINES[3], 'c20'),
];

export const MACHINE_BY_ID: Record<MachineId, MachineDef> = Object.fromEntries(
  MACHINES.map((m) => [m.id, m]),
) as Record<MachineId, MachineDef>;

/** Nombre d'affixes par rareté — MVP_DESIGN §3.2. */
export const AFFIX_COUNT: Record<Rarity, number> = {
  common: 0,
  uncommon: 1,
  rare: 2,
  epic: 3,
  legendary: 4,
  prismatic: 4,
};

/**
 * Valeur de revente, en pourcentage du prix machine — MVP_DESIGN §3.4.
 * v0.2.3 (COMMENTS §2.3) : multiplicateurs fortement réduits — le Recycleur
 * finançait un nombre quasi illimité de relances au Gachapon.
 */
export const RECYCLE_PERCENT: Record<Rarity, number> = {
  common: 0.1,
  uncommon: 0.25,
  rare: 0.5,
  epic: 1,
  legendary: 2,
  prismatic: 5,
};

/** Ordre croissant de rareté (comparaisons, tri d'inventaire). */
export const RARITY_ORDER: Rarity[] = [
  'common',
  'uncommon',
  'rare',
  'epic',
  'legendary',
  'prismatic',
];

export const RARITY_NAME: Record<Rarity, string> = {
  common: 'Commun',
  uncommon: 'Inhabituel',
  rare: 'Rare',
  epic: 'Épique',
  legendary: 'Légendaire',
  prismatic: 'Prismatique',
};

/** Machine « d'origine » d'un dé, pour la valeur de recyclage. */
export function machineForSides(sides: number): MachineDef {
  return MACHINES.find((m) => m.sides === sides) ?? MACHINES[0];
}
